import React, { useState, useMemo } from 'react';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { QuickBooksSyncHint } from './QuickBooksSyncHint';
import { Button } from './ui/button';
import ChipTabs from './ui/chip-tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Calendar,
  Mail,
  Ticket,
} from 'lucide-react';
import { toast } from 'sonner';
import { useFinancialBookings } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusChipClass = (status) => {
  if (status === 'refunded') return 'bg-gray-100 text-gray-500';
  if (status === 'paid' || status === 'completed' || status === 'confirmed') return 'bg-green-100 text-green-700';
  if (status === 'partial') return 'bg-amber-100 text-amber-700';
  return 'bg-blue-100 text-blue-700';
};

// Money row inside the detail breakdown
const MoneyRow = ({ label, value, sub, negative, bold, testid }) => (
  <div className="flex items-center justify-between gap-3 py-1.5" data-testid={testid}>
    <div className="min-w-0">
      <span className={`text-sm ${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
      {sub && <span className="block text-[11px] text-gray-400">{sub}</span>}
    </div>
    <span className={`text-sm font-medium tabular-nums whitespace-nowrap ${
      bold ? 'text-emerald-600 font-bold text-base' : negative ? 'text-gray-500' : 'text-gray-900'
    }`}>
      {negative ? '− ' : ''}{value}
    </span>
  </div>
);

// Revenue detail modal — the full money story for one booking
const RevenueDetailDialog = ({ booking, currency, onClose }) => {
  if (!booking) return null;
  const status = booking.paymentStatus || booking.payment_status || booking.status;
  const customerTotal = booking.customerTotal ?? booking.totalAmount ?? 0;
  const stripeFee = booking.stripeFee ?? 0;
  const platformFee = booking.platformFee ?? 0;
  const ownerRevenue = booking.ownerRevenue ?? 0;
  const subtotal = booking.subtotal ?? 0;
  const salesTax = booking.salesTax ?? 0;
  const amountPaid = booking.amountPaid ?? 0;
  const depositAmount = booking.depositAmount ?? 0;
  const isPartial = status === 'partial';
  const piId = booking.stripePaymentIntentId;

  return (
    <Dialog open={!!booking} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="revenue-detail-modal">
        <DialogHeader>
          <DialogTitle className="pr-8">{booking.serviceName || 'Booking'}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap">
            <span>{booking.customerName || 'Customer'}</span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusChipClass(status)}`} data-testid="revenue-detail-status">
              {status}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* Booking facts */}
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-1.5 min-w-0">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span className="truncate">{formatDate(booking.bookingDate || booking.booking_date)}</span>
          </div>
          {booking.confirmationCode && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Ticket className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="font-mono truncate" data-testid="revenue-detail-code">{booking.confirmationCode}</span>
            </div>
          )}
          {booking.customerEmail && (
            <div className="flex items-center gap-1.5 min-w-0 col-span-2">
              <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{booking.customerEmail}</span>
            </div>
          )}
        </div>

        {/* Money breakdown */}
        <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3" data-testid="revenue-detail-breakdown">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Money breakdown</p>
          <MoneyRow label="Customer paid" value={formatCurrency(customerTotal, currency)} testid="revenue-detail-customer-paid" />
          {subtotal > 0 && subtotal !== customerTotal && (
            <MoneyRow label="Subtotal" value={formatCurrency(subtotal, currency)} />
          )}
          {salesTax > 0 && <MoneyRow label="Tax collected" value={formatCurrency(salesTax, currency)} />}
          <div className="border-t border-gray-200 my-1.5" />
          <MoneyRow
            label="Stripe processing fee"
            sub="estimated — exact fee shown in Stripe"
            value={formatCurrency(stripeFee, currency)}
            negative
            testid="revenue-detail-stripe-fee"
          />
          <MoneyRow
            label="BookingFlows platform fee"
            value={formatCurrency(platformFee, currency)}
            negative
            testid="revenue-detail-platform-fee"
          />
          <div className="border-t border-gray-200 my-1.5" />
          <MoneyRow label="Your net revenue" value={formatCurrency(ownerRevenue, currency)} bold testid="revenue-detail-net" />
        </div>

        {/* Deposit / partial-payment context */}
        {isPartial && (
          <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3 text-xs text-amber-800" data-testid="revenue-detail-partial">
            <span className="font-semibold">Partially paid.</span>{' '}
            {formatCurrency(amountPaid || depositAmount, currency)} collected so far —{' '}
            {formatCurrency(Math.max(customerTotal - (amountPaid || depositAmount), 0), currency)} still due.
          </div>
        )}
        {status === 'refunded' && (
          <div className="rounded-xl bg-gray-50 border border-gray-100 px-4 py-3 text-xs text-gray-500" data-testid="revenue-detail-refunded">
            This booking was refunded — it's excluded from your revenue totals.
          </div>
        )}

        {/* Stripe link */}
        {piId && (
          <a
            href={`https://dashboard.stripe.com/payments/${piId}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="revenue-view-in-stripe"
            className="inline-flex items-center justify-center gap-1.5 w-full h-10 rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            View payment in Stripe
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RevenueTab = ({ onNavigate }) => {
  // Date range - default to current year
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const ITEMS_PER_PAGE = 15;
  
  // Currency from the operator's country (single source of truth)
  const { currencyCode: currency } = useOperatorLocale();
  
  // Fetch via shared financial hook — same canonical cache key as P&L, so the
  // two tabs dedupe to ONE network request and can never diverge.
  const { raw: bookingsData, summary, isLoading, isError: error } = useFinancialBookings(
    dateRange.startDate,
    dateRange.endDate
  );
  
  // Filter to only show paid/partial/refunded bookings (revenue-generating statuses)
  const allBookings = bookingsData?.bookings || [];
  const revenueBookings = allBookings.filter(b => 
    ['paid', 'partial', 'refunded', 'completed', 'confirmed'].includes(b.paymentStatus || b.payment_status || b.status)
  );

  // Status chip filter (display-only — never changes money totals)
  const bookings = useMemo(() => {
    if (!statusFilter || statusFilter === 'all') return revenueBookings;
    return revenueBookings.filter(b => {
      const s = b.paymentStatus || b.payment_status || b.status;
      if (statusFilter === 'paid') return ['paid', 'completed', 'confirmed'].includes(s);
      return s === statusFilter;
    });
  }, [revenueBookings, statusFilter]);

  const chipCounts = useMemo(() => {
    const c = { paid: 0, partial: 0, refunded: 0 };
    revenueBookings.forEach(b => {
      const s = b.paymentStatus || b.payment_status || b.status;
      if (['paid', 'completed', 'confirmed'].includes(s)) c.paid += 1;
      else if (s === 'partial') c.partial += 1;
      else if (s === 'refunded') c.refunded += 1;
    });
    return c;
  }, [revenueBookings]);
  
  // 🔒 SSOT: money total comes ONLY from the backend-computed summary
  // (financial_utils). We never recompute it client-side — count/avg are
  // display-only derivations, not money.
  const totals = useMemo(() => {
    const count = revenueBookings.filter(b => {
      const status = b.paymentStatus || b.payment_status || b.status;
      return status !== 'refunded';
    }).length;
    const total = summary
      ? (summary.totalOwnerRevenue ?? summary.totalRevenue ?? 0)
      : 0;
    const avgBooking = count > 0 ? total / count : 0;
    return { total, count, avgBooking };
  }, [revenueBookings, summary]);
  
  // Pagination
  const totalPages = Math.ceil(bookings.length / ITEMS_PER_PAGE);
  const paginatedBookings = bookings.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  
  // Export functions
  const exportCSV = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Customer', 'Service', 'Revenue', 'Status'];
    const rows = bookings.map(b => [
      b.bookingDate || b.booking_date || '',
      (b.customerName || b.customer_name || 'N/A').replace(/,/g, ' '),
      (b.serviceName || b.service_name || 'N/A').replace(/,/g, ' '),
      b.ownerRevenue || b.totalAmount || b.total_amount || 0,
      b.paymentStatus || b.payment_status || b.status || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('Revenue exported as CSV!');
  };
  
  const exportJSON = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: { totalRevenue: totals.total, bookingCount: totals.count },
      bookings: bookings.map(b => ({
        date: b.bookingDate || b.booking_date,
        customer: b.customerName || b.customer_name,
        service: b.serviceName || b.service_name,
        revenue: b.ownerRevenue || b.totalAmount || b.total_amount || 0,
        status: b.paymentStatus || b.payment_status || b.status
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('Revenue exported as JSON!');
  };
  
  const exportPDF = () => {
    if (bookings.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Create printable HTML
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Revenue Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; border-bottom: 1px solid #10b981; padding-bottom: 10px; }
          .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f9fafb; text-align: left; padding: 12px; border-bottom: 1px solid #e5e7eb; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          .revenue { color: #10b981; text-align: right; }
          .status { padding: 4px 8px; border-radius: 12px; font-size: 12px; }
          .paid { background: #dcfce7; color: #166534; }
          .refunded { background: #f3f4f6; color: #6b7280; }
        </style>
      </head>
      <body>
        <h1>Revenue Report</h1>
        <p>Period: ${dateRange.startDate} to ${dateRange.endDate}</p>
        <div class="summary">
          <strong>Total Revenue:</strong> ${formatCurrency(totals.total, currency)}<br>
          <strong>Bookings:</strong> ${totals.count}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer</th>
              <th>Service</th>
              <th style="text-align:right">Revenue</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${bookings.map(b => {
              const status = b.paymentStatus || b.payment_status || b.status;
              return `
                <tr>
                  <td>${formatDate(b.bookingDate || b.booking_date)}</td>
                  <td>${b.customerName || b.customer_name || '—'}</td>
                  <td>${b.serviceName || b.service_name || '—'}</td>
                  <td class="revenue">${formatCurrency(b.ownerRevenue || b.totalAmount || 0, currency)}</td>
                  <td><span class="status ${status === 'paid' ? 'paid' : 'refunded'}">${status}</span></td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load revenue data');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Revenue" subtitle="Every dollar earned through BookingFlows">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  return (
    <TabPageLayout 
      title="Revenue" 
      subtitle="Every dollar earned through BookingFlows"
      actions={
        <ExportMenu onExport={(f) => ({ csv: exportCSV, pdf: exportPDF, json: exportJSON }[f]())} />
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker + Total */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <DateRangeControl
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onStartChange={(v) => { setDateRange({ ...dateRange, startDate: v }); setPage(1); }}
            onEndChange={(v) => { setDateRange({ ...dateRange, endDate: v }); setPage(1); }}
          />
          
          {/* Total Revenue Badge */}
          <div className="px-4 py-2 rounded-full bg-emerald-50" data-testid="revenue-total-badge">
            <span className="text-lg font-medium text-emerald-700">
              {formatCurrency(totals.total, currency)}
            </span>
            <span className="text-sm ml-2 text-emerald-600/70">
              Total Revenue
            </span>
          </div>
        </div>

        <QuickBooksSyncHint variant="revenue" onNavigate={onNavigate} />

        {/* Status filter chips */}
        <ChipTabs
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          testIdPrefix="revenue-filter"
          tabs={[
            { value: 'all', label: 'All' },
            { value: 'paid', label: 'Paid', color: 'green', count: chipCounts.paid },
            { value: 'partial', label: 'Partial', color: 'amber', count: chipCounts.partial },
            { value: 'refunded', label: 'Refunded', color: 'gray', count: chipCounts.refunded },
          ]}
        />

        {/* Bookings Table */}
        <div className="card-soft overflow-hidden">
          {bookings.length === 0 ? (
            <EmptyState
              icon={<TrendingUp className="w-10 h-10" />}
              title={statusFilter !== 'all' ? 'Nothing matches this filter' : 'No revenue yet'}
              description={statusFilter !== 'all' ? 'Try a different status filter' : 'Paid bookings will appear here'}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedBookings.map((booking, i) => {
                      const status = booking.paymentStatus || booking.payment_status || booking.status;
                      const isRefunded = status === 'refunded';
                      return (
                        <tr
                          key={booking.id}
                          onClick={() => setSelectedBooking(booking)}
                          data-testid={`revenue-row-${i}`}
                          className={`${isRefunded ? 'opacity-50' : ''} hover:bg-gray-50 transition-colors cursor-pointer`}
                        >
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {formatDate(booking.bookingDate || booking.booking_date)}
                          </td>
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {booking.customerName || booking.customer_name || '—'}
                          </td>
                          <td className={`px-4 py-3 ${isRefunded ? 'text-gray-500' : 'text-gray-900'}`}>
                            {booking.serviceName || booking.service_name || '—'}
                          </td>
                          <td className={`px-4 py-3 text-right ${isRefunded ? 'text-gray-500 line-through' : 'text-green-600'}`}>
                            {formatCurrency(booking.ownerRevenue || booking.totalAmount || booking.total_amount, currency)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${statusChipClass(status)}`}>
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, bookings.length)} of {bookings.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-9"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Revenue detail modal */}
      <RevenueDetailDialog
        booking={selectedBooking}
        currency={currency}
        onClose={() => setSelectedBooking(null)}
      />
    </TabPageLayout>
  );
};

export default RevenueTab;
